import { useApp } from '@/contexts';

import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';

import type { CronScheduleBuilderHandle } from '@/components/cron';
import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { useGetEnvironmentsQuery } from '@/features/environments/api/environmentsApi';
import { useGetConfigTemplatesQuery } from '@/features/environments/api/environmentsApi';
import { ScheduleConfigPanel } from '@/features/schedules/components/ScheduleConfigPanel';
import { buildScheduleTriggerRequest } from '@/features/schedules/utils/buildScheduleTriggerRequest';
import type { CronScheduleSaveState } from '@/features/schedules/utils/cronScheduleSave';
import {
  DEFAULT_CRON_EXPRESSION,
  getDefaultCronTimeZone,
  getRecipeEditorDailyDefaults,
  getRecipeEditorRepeatingDefaults,
} from '@/features/schedules/utils/scheduleDefaults';
import { isScheduleConfigValid } from '@/features/schedules/utils/scheduleFormSchema';
import {
  useGetTransformerDeploymentsQuery,
  useGetTransformerVersionsQuery,
  useSaveTriggerMutation,
} from '@/features/transformers/api/transformersApi';
import type { ScheduleType } from '@/types/schedule';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { logger } from '@/utils/logger';

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  recipeBaseId: string;
  tenantId: string;
  showTemplateSelection?: boolean;
}

type DailyFormState = ReturnType<typeof getRecipeEditorDailyDefaults>;
type RepeatingFormState = ReturnType<typeof getRecipeEditorRepeatingDefaults>;

/**
 * Dialog for creating a recipe schedule/trigger from the Recipe Editor.
 *
 * This dialog is create-only and always allows creating a schedule, regardless
 * of whether one already exists (matches legacy dataProtect behaviour, where
 * save always POSTs to `createtrigger`). Editing or removing an existing
 * schedule is handled in the dedicated Schedules tab.
 */
export const ScheduleDialog = ({
  open,
  onClose,
  recipeBaseId,
  tenantId,
  showTemplateSelection = false,
}: ScheduleDialogProps) => {
  const { appId } = useApp();
  const { showSnackbar } = useSnackbar();
  const [scheduleType, setScheduleType] = useState<ScheduleType>('daily');
  const [error, setError] = useState<string | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const cronBuilderRef = useRef<CronScheduleBuilderHandle>(null);

  const AUTO_DISMISS_MS = 5000;

  useEffect(() => {
    if (error) {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => setError(null), AUTO_DISMISS_MS);
    }
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [error]);

  const [selectedEnvId, setSelectedEnvId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [daily, setDaily] = useState<DailyFormState>(getRecipeEditorDailyDefaults);
  const [repeating, setRepeating] = useState<RepeatingFormState>(getRecipeEditorRepeatingDefaults);
  const [cron, setCron] = useState<CronScheduleSaveState>({
    expression: DEFAULT_CRON_EXPRESSION,
    explain: '',
    timeZone: getDefaultCronTimeZone(),
  });

  const { data: environments = [] } = useGetEnvironmentsQuery(
    { tenantId, appId },
    { skip: !open || !tenantId || !appId }
  );
  const { data: recipeDeployments = [] } = useGetTransformerDeploymentsQuery(
    { appId, baseId: recipeBaseId },
    { skip: !open || !appId || !recipeBaseId }
  );
  const { data: recipeVersions = [] } = useGetTransformerVersionsQuery(
    { transBaseId: recipeBaseId, appId },
    { skip: !open || !appId || !recipeBaseId }
  );

  const deployedEnvironmentIds = new Set(recipeDeployments.map((d) => String(d.environmentId)));

  const deployedVersionByEnvId = new Map<string, string>();
  recipeDeployments.forEach((d) => {
    const ver = recipeVersions.find((v) => v.id === d.transId);
    if (ver) {
      deployedVersionByEnvId.set(String(d.environmentId), String(ver.version));
    }
  });

  const deployedEnvironments = environments.filter((env) =>
    deployedEnvironmentIds.has(String(env.id))
  );
  const { data: templates = [] } = useGetConfigTemplatesQuery(undefined, {
    skip: !showTemplateSelection,
  });

  const [saveTrigger, { isLoading: isSaving }] = useSaveTriggerMutation();

  useEffect(() => {
    if (!open) return;
    const firstDeployedEnv = environments.find((env) => deployedEnvironmentIds.has(String(env.id)));
    setSelectedEnvId(firstDeployedEnv?.id?.toString() || '');
    setSelectedTemplate('');
    setScheduleType('daily');
    setDaily(getRecipeEditorDailyDefaults());
    setRepeating(getRecipeEditorRepeatingDefaults());
    setCron({
      expression: DEFAULT_CRON_EXPRESSION,
      explain: '',
      timeZone: getDefaultCronTimeZone(),
    });
  }, [open, environments, recipeDeployments]);

  const handleSave = async () => {
    if (!selectedEnvId) {
      setError('Please select an environment');
      return;
    }

    if (!isScheduleConfigValid(scheduleType, daily, repeating, cron)) {
      if (scheduleType === 'repeating' && repeating.startingAt && !repeating.timeZone.trim()) {
        setError('Please select a timezone when setting a start time');
        return;
      }
      if (scheduleType === 'cron') {
        setError('Invalid cron expression. Please check the errors and try again.');
        return;
      }
      setError('Please complete the schedule configuration');
      return;
    }

    try {
      setError(null);
      const triggerData = buildScheduleTriggerRequest({
        mode: 'create',
        scheduleType,
        daily,
        repeating,
        cron,
        cronBuilderRef,
        baseIds: {
          baseid: parseInt(recipeBaseId, 10),
          environmentId: parseInt(selectedEnvId, 10),
        },
        templateName: selectedTemplate || undefined,
      });

      await saveTrigger({ tenantId, data: triggerData }).unwrap();
      showSnackbar('Schedule created successfully', 'success');
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : getApiErrorMessage(err, 'Failed to save schedule');
      setError(message);
      logger.error('Failed to save trigger:', err);
    }
  };

  const hasDeployedEnvironments = deployedEnvironments.length > 0;
  const canCreateSchedule = !!selectedEnvId;
  const sortedTemplates = [...templates].sort((a, b) => a.name.localeCompare(b.name));
  const isConfigValid = isScheduleConfigValid(scheduleType, daily, repeating, cron);

  const saveButtonText = isSaving ? 'Saving...' : 'Save';

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>Schedules</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {!hasDeployedEnvironments && (
            <Alert severity="warning">
              Please deploy the recipe to an environment before creating a schedule.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth disabled={!hasDeployedEnvironments}>
            <InputLabel id="environment-select-label">Environment</InputLabel>
            <Select
              labelId="environment-select-label"
              value={selectedEnvId}
              onChange={(e) => setSelectedEnvId(e.target.value)}
              label="Environment"
            >
              {deployedEnvironments.length === 0 ? (
                <MenuItem value="" disabled>
                  <em>No deployed environments</em>
                </MenuItem>
              ) : (
                deployedEnvironments.map((env) => {
                  const deployedVersion = deployedVersionByEnvId.get(String(env.id));
                  return (
                    <MenuItem key={env.id} value={env.id.toString()}>
                      {env.name}
                      {deployedVersion && (
                        <Box
                          component="span"
                          sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}
                        >
                          {`(Deployed Ver. ${deployedVersion})`}
                        </Box>
                      )}
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>

          {showTemplateSelection && (
            <FormControl fullWidth disabled={!canCreateSchedule}>
              <InputLabel id="template-select-label">Template (Optional)</InputLabel>
              <Select
                labelId="template-select-label"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                label="Template (Optional)"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {sortedTemplates.map((template) => (
                  <MenuItem key={template.name} value={template.name}>
                    {template.name.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <ScheduleConfigPanel
            scheduleType={scheduleType}
            onScheduleTypeChange={setScheduleType}
            daily={daily}
            onDailyChange={setDaily}
            repeating={repeating}
            onRepeatingChange={setRepeating}
            cron={cron}
            onCronChange={setCron}
            cronBuilderRef={cronBuilderRef}
            typeSelectorVariant="tabs"
            showLegacyMigrationNotice={false}
            disabled={!canCreateSchedule || isSaving}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="error" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !selectedEnvId || !canCreateSchedule || !isConfigValid}
          >
            {saveButtonText}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
